/** GERP CREDIT NOTE(156) — 레거시 appr_edit_forms_credit_note_mstr */
export const CREDIT_NOTE_TYPE = 156;

export const isCreditNoteType = (apvType: number): boolean =>
  apvType === CREDIT_NOTE_TYPE;

export const CREDIT_NOTE_MISSING_MESSAGE =
  '데이터를 로드하지 않았습니다. 조회 조건 선택 후 확인 버튼을 누르세요.';

/** 레거시 pre_cost_yn — GERP 에서 상신할 수 없는 건(draft-source 422) */
export const CREDIT_NOTE_BLOCKED_MESSAGE =
  '현재 결재가 회수상태 입니다.\n회수 상태에서는 결재내역이 추가 불가능합니다.';

/** GERP 미상신 결재 건 — 키는 GERP 응답 그대로 */
export type CreditNoteCandidate = {
  sysId: string;
  aprvId: string;
  aprvTypcd: string;
  aprvRev?: string | null;
  title?: string | null;
  docTit?: string | null;
  realCreditNo?: string | null;
};

type Value = string | number | null | undefined;

/** 레거시 CREDIT_HEADER — 브랜드별 1행 */
export type CreditNoteHeader = {
  brand?: Value;
  brandNm?: Value;
  custCd?: Value;
  custNm?: Value;
  typeCd?: Value;
  typeNm?: Value;
  styleCnt?: Value;
  colorCnt?: Value;
  creditQty?: Value;
  creditAmt?: Value;
  currCd?: Value;
};

/** OZ 리포트 form POST 값 */
export type CreditNoteOzPrint = {
  url?: string | null;
  gerpOzrNm?: string | null;
  gerpOdiNm?: string | null;
  pcount?: Value;
  args1?: string | null;
  locale?: string | null;
};

export type CreditNoteCard = {
  aprvId: string;
  creditNoBiz?: string | null;
  realCreditNo?: string | null;
  headers?: CreditNoteHeader[] | null;
  ozPrint?: CreditNoteOzPrint | null;
};

export type CreditNoteDraftSource = {
  code: number;
  attributes: Record<string, unknown>;
  display: {
    candidates: CreditNoteCandidate[] | null;
    selection: string[] | null;
    subject: string | null;
    content: { cards?: CreditNoteCard[] | null } | null;
  };
};

/** 작성 중 고른 건 1개 */
export type CreditNoteItem = {
  aprvId: string;
  /** 레거시 data-credit_no — 건 제목(DOC_TIT = CREDIT_NO_BIZ) */
  label: string;
  /** 부분반려 모달 제목 차수 — 미상신 목록에만 있음 */
  aprvRev: string;
  card: CreditNoteCard;
};

/** 저장 attributes 와 같은 모양 */
export type CreditNoteState = {
  systemId: string;
  approvalTypeCode: string;
  /** 고른 건, 쉼표로 이음 */
  approvalId: string;
  /** 작성 시점 카드 HTML */
  content: string;
};

/** 레거시 콤보 문구 — 건 제목(DOC_TIT) */
export const buildCreditNoteLabel = (c: CreditNoteCandidate) =>
  c.docTit?.trim() || c.title?.trim() || c.aprvId;

/** 레거시 change_title_content — 고른 순서의 첫 건 기준. "외" 앞 공백 없음도 레거시 그대로 */
export const buildCreditNoteSubject = (
  labels: string[],
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  if (!labels.length) return '';
  const [first] = labels;
  if (labels.length === 1)
    return t('Credit Note ({{title}})', { title: first });
  return t('Credit Note ({{title}}외 {{count}}건)', {
    title: first,
    count: labels.length - 1,
  });
};

/** 레거시 본문 — 고를 때마다 덮어씀 */
export const buildCreditNoteEditorContent = (
  labels: string[],
  t: (key: string) => string,
): string =>
  `<p>&nbsp;</p><p>${t('추가')} : ${labels.join(
    ',',
  )}&nbsp;</p><p>&nbsp;</p><p>&nbsp;</p>`;

/** 응답 카드 중 고른 건 — 없으면 첫 카드 */
export const pickCreditNoteCard = (
  source: CreditNoteDraftSource,
  aprvId: string,
): CreditNoteCard | null => {
  const cards = source.display.content?.cards ?? [];
  return cards.find((c) => c.aprvId === aprvId) ?? cards[0] ?? null;
};

const nonEmpty = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v : null;

export const parseCreditNoteAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): CreditNoteState | null => {
  const content = nonEmpty(attributes?.content);
  if (!attributes || !content) return null;
  return {
    systemId: nonEmpty(attributes.systemId) ?? '',
    approvalTypeCode: nonEmpty(attributes.approvalTypeCode) ?? '',
    approvalId: nonEmpty(attributes.approvalId) ?? '',
    content,
  };
};

export const parseCreditNoteContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => nonEmpty(attributes?.content);

export const buildCreditNoteAttributes = (
  state: CreditNoteState | null | undefined,
) =>
  state
    ? {
        systemId: state.systemId,
        approvalTypeCode: state.approvalTypeCode,
        approvalId: state.approvalId,
        content: state.content,
      }
    : null;

export const checkCreditNoteBeforeSave = (
  state: CreditNoteState | null | undefined,
): string | null =>
  state?.content && state.approvalId ? null : CREDIT_NOTE_MISSING_MESSAGE;

export const CREDIT_NOTE_REJECT_REASON_MAX_LENGTH = 300;

export const creditNoteKey = (state: CreditNoteState | null | undefined) =>
  state?.approvalId ?? '';

/** 레거시 문서 DETAIL 버튼은 REAL_CREDIT_NO 만 들고 있어 나머지는 레거시 고정값 */
export const LEGACY_CREDIT_NOTE_OZ: Required<Omit<CreditNoteOzPrint, 'args1'>> =
  {
    url: 'https://report.fnf.co.kr/report/gerp_oz.jsp',
    gerpOzrNm: 'rtn_credit_note.ozr',
    gerpOdiNm: 'rtn_credit_note',
    pcount: 1,
    locale: 'en_US',
  };

/** 레거시 btn-printsc — OZ 리포트를 새 창(SCMReport)에 form POST */
export const openCreditNoteOzReport = (oz: CreditNoteOzPrint) => {
  const url = oz.url?.trim() ?? '';
  // 저장 HTML 값이라 OZ 리포트 서버 주소만 허용
  if (
    !url.startsWith('https://report.fnf.co.kr/') ||
    !oz.gerpOdiNm ||
    !oz.args1
  )
    return;
  const target = 'SCMReport';
  window.open(
    '',
    target,
    'top=100,left=100,width=900,height=800,location=no,toolbar=no,status=no',
  );
  const form = document.createElement('form');
  form.method = 'post';
  form.action = url;
  form.target = target;
  const fields: Record<string, string> = {
    gerp_ozr_nm: oz.gerpOzrNm ?? '',
    gerp_odi_nm: oz.gerpOdiNm,
    [`odi.${oz.gerpOdiNm}.args1`]: oz.args1,
    [`odi.${oz.gerpOdiNm}.pcount`]: String(oz.pcount || 1),
    locale: oz.locale || 'en_US',
  };
  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
  form.remove();
};
